import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { PageLayout } from "lib";
import { EventsAPI } from "api_inbusiness";
import { toast } from "react-toastify";
import { useUser } from "hooks/useUser";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Box,
  Button,
  TextField,
  Autocomplete,
  Divider,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BlockIcon from "@mui/icons-material/Block";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EventIcon from "@mui/icons-material/Event";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styles from "./EventCheckinReport.module.scss";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const EventCheckinReport = () => {
  const history = useHistory();
  const { permissionsData } = useUser();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);

  // Redirect non-admin users to home
  useEffect(() => {
    if (!permissionsData.seeAdminButtons) {
      history.push("/");
    }
  }, [permissionsData.seeAdminButtons, history]);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const res = await EventsAPI.getCustomEvents(500, 0, 1);
      if (res.ok && res.data?.content) {
        const sortedEvents = res.data.content.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setEvents(sortedEvents);
      }
    } catch (error) {
      console.error("Error loading events:", error);
    }
    setLoadingEvents(false);
  };

  const handleEventChange = async (event, newValue) => {
    setSelectedEvent(newValue);
    if (!newValue) {
      setReportData(null);
      return;
    }

    setLoading(true);
    try {
      const res = await EventsAPI.getEventReport(newValue.id);
      if (res.ok) {
        setReportData(res.data);
      } else {
        toast.error("Eroare la încărcarea raportului");
      }
    } catch (error) {
      console.error("Error loading report:", error);
      toast.error("Eroare la încărcarea raportului");
    }
    setLoading(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("ro-RO", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getEventYear = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).getFullYear();
  };

  // Chart.js configuration with InBusiness light theme styling
  const getChartData = () => {
    const breakdown = reportData?.checkinFlow?.breakdown || [];
    return {
      labels: breakdown.map((item) => `${item.hour}:00`),
      datasets: [
        {
          label: "Scanări",
          data: breakdown.map((item) => item.count),
          backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 200);
            gradient.addColorStop(0, "rgba(91, 128, 186, 0.9)");
            gradient.addColorStop(1, "rgba(74, 111, 165, 0.6)");
            return gradient;
          },
          borderColor: "rgba(91, 128, 186, 1)",
          borderWidth: 0,
          borderRadius: 8,
          hoverBackgroundColor: "rgba(91, 128, 186, 1)",
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(30, 30, 30, 0.95)",
        titleFont: { size: 14, weight: "600" },
        bodyFont: { size: 13 },
        padding: 14,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context) => `Ora ${context[0].label}`,
          label: (context) => `${context.parsed.y} scanări`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#64748b",
          font: { size: 12 },
        },
        border: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(226, 232, 240, 0.8)",
        },
        ticks: {
          color: "#64748b",
          font: { size: 12 },
          stepSize: 1,
        },
        border: {
          display: false,
        },
      },
    },
  };

  const handleExportPDF = async () => {
    if (!selectedEvent) return;
    
    setLoading(true);
    try {
      const { ok, data, error } = await EventsAPI.downloadEventReportPDF(selectedEvent.id);
      
      if (ok && data) {
        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Raport_Checkin_${reportData?.eventTitle || 'Eveniment'}.pdf`;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success("Raportul PDF a fost descărcat cu succes!");
      } else {
        console.error("PDF export error:", error);
        toast.error("Eroare la generarea raportului PDF!");
      }
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Eroare la generarea raportului PDF!");
    } finally {
      setLoading(false);
    }
  };

  // Stats cards config
  const statsCards = reportData
    ? [
        {
          id: "total",
          label: "Total Bilete",
          value: reportData.summary?.totalTickets || 0,
          icon: ConfirmationNumberIcon,
          color: "primary",
        },
        {
          id: "scanned",
          label: "Bilete Scanate",
          value: reportData.summary?.ticketsScanned || 0,
          subtext: `${reportData.summary?.participationRate || 0}% rată participare`,
          icon: QrCodeScannerIcon,
          color: "success",
        },
        {
          id: "revenue",
          label: "Revenue Total",
          value: `${(reportData.summary?.revenue || 0).toLocaleString("ro-RO")} RON`,
          icon: AttachMoneyIcon,
          color: "gold",
        },
        {
          id: "peak",
          label: "Ora de Vârf",
          value: reportData.checkinFlow?.peakHour
            ? `${reportData.checkinFlow.peakHour.hour}:00`
            : "-",
          subtext: reportData.checkinFlow?.peakHour
            ? `${reportData.checkinFlow.peakHour.count} scanări`
            : null,
          icon: TrendingUpIcon,
          color: "purple",
        },
      ]
    : [];

  return (
    <>
      <Helmet>
        <title>InBusiness CRM :: Raport Check-in</title>
      </Helmet>
      <PageLayout title="">
        <div className={styles.pageContainer}>
          {/* Header Section */}
          <header className={styles.pageHeader}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/reports")}
              className={styles.backButton}
            >
              Înapoi la Rapoarte
            </Button>

            <div className={styles.headerContent}>
              <div className={styles.headerTitles}>
                <Typography variant="h4" className={styles.pageTitle}>
                  Raport Check-in Evenimente
                </Typography>
                <Typography className={styles.pageSubtitle}>
                  Analizează participarea și fluxul de check-in pentru evenimentele tale
                </Typography>
              </div>
            </div>
          </header>

          {/* Event Selector */}
          <section className={styles.selectorSection}>
            <div className={styles.selectorCard}>
              <div className={styles.selectorIcon}>
                <EventIcon />
              </div>
              <Autocomplete
                options={events}
                value={selectedEvent}
                onChange={handleEventChange}
                getOptionLabel={(option) =>
                  option ? `${option.title}` : ""
                }
                groupBy={(option) => getEventYear(option.date)}
                loading={loadingEvents}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                className={styles.autocomplete}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Caută și selectează un eveniment..."
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <SearchIcon className={styles.searchIcon} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} className={styles.optionItem}>
                    <div className={styles.optionContent}>
                      <span className={styles.optionTitle}>{option.title}</span>
                      <span className={styles.optionDate}>
                        {formatShortDate(option.date)}
                      </span>
                    </div>
                  </li>
                )}
                noOptionsText="Niciun eveniment găsit"
                loadingText="Se încarcă evenimentele..."
              />
            </div>
          </section>

          {/* Loading State */}
          {loading && (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}>
                <CircularProgress size={48} thickness={3} />
              </div>
              <Typography className={styles.loadingText}>
                Se încarcă raportul...
              </Typography>
            </div>
          )}

          {/* Report Content */}
          {reportData && !loading && (
            <div className={styles.reportContainer}>
              {/* Event Info Header */}
              <section className={styles.eventInfoSection}>
                <div className={styles.eventInfo}>
                  <Typography variant="h5" className={styles.eventTitle}>
                    {reportData.eventTitle}
                  </Typography>
                  <div className={styles.eventMeta}>
                    <Chip
                      icon={<EventIcon />}
                      label={formatDate(reportData.eventDate)}
                      className={styles.dateChip}
                    />
                    {reportData.summary?.ticketsInvalid > 0 && (
                      <Chip
                        icon={<BlockIcon />}
                        label={`${reportData.summary.ticketsInvalid} bilete invalidate`}
                        className={styles.invalidChip}
                      />
                    )}
                  </div>
                </div>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handleExportPDF}
                  className={styles.printButton}
                >
                  Exportă Raport
                </Button>
              </section>

              {/* Stats Grid */}
              <section className={styles.statsSection}>
                <div className={styles.statsGrid}>
                  {statsCards.map((stat) => (
                    <div
                      key={stat.id}
                      className={`${styles.statCard} ${styles[`stat${stat.color}`]}`}
                    >
                      <div className={styles.statIcon}>
                        <stat.icon />
                      </div>
                      <div className={styles.statContent}>
                        <span className={styles.statLabel}>{stat.label}</span>
                        <span className={styles.statValue}>{stat.value}</span>
                        {stat.subtext && (
                          <span className={styles.statSubtext}>{stat.subtext}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Charts Section */}
              <section className={styles.chartsSection}>
                <Grid container spacing={3}>
                  {/* Check-in Flow Chart */}
                  <Grid item xs={12} lg={8}>
                    <Card className={styles.chartCard}>
                      <CardContent>
                        <div className={styles.chartHeader}>
                          <div>
                            <Typography className={styles.chartTitle}>
                              Flux Check-in
                            </Typography>
                            <Typography className={styles.chartSubtitle}>
                              Distribuția scanărilor pe ore
                            </Typography>
                          </div>
                        </div>
                        <div className={styles.chartWrapper}>
                          {reportData.checkinFlow?.breakdown?.length > 0 ? (
                            <Bar data={getChartData()} options={chartOptions} />
                          ) : (
                            <div className={styles.noDataMessage}>
                              <QrCodeScannerIcon />
                              <Typography>
                                Nu există date de check-in pentru acest eveniment
                              </Typography>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Timing Card */}
                  <Grid item xs={12} lg={4}>
                    <Card className={styles.timingCard}>
                      <CardContent>
                        <Typography className={styles.chartTitle}>
                          Timing Check-in
                        </Typography>
                        <Typography className={styles.chartSubtitle}>
                          Prima și ultima scanare
                        </Typography>
                        
                        <div className={styles.timingContent}>
                          <div className={styles.timingItem}>
                            <div className={styles.timingIcon + " " + styles.firstScan}>
                              <AccessTimeIcon />
                            </div>
                            <div className={styles.timingInfo}>
                              <span className={styles.timingLabel}>Prima Scanare</span>
                              <span className={styles.timingValue}>
                                {formatTime(reportData.timing?.firstScan)}
                              </span>
                            </div>
                          </div>

                          <Divider className={styles.timingDivider} />

                          <div className={styles.timingItem}>
                            <div className={styles.timingIcon + " " + styles.lastScan}>
                              <AccessTimeIcon />
                            </div>
                            <div className={styles.timingInfo}>
                              <span className={styles.timingLabel}>Ultima Scanare</span>
                              <span className={styles.timingValue}>
                                {formatTime(reportData.timing?.lastScan)}
                              </span>
                            </div>
                          </div>

                          {reportData.timing?.firstScan && reportData.timing?.lastScan && (
                            <>
                              <Divider className={styles.timingDivider} />
                              <div className={styles.durationInfo}>
                                <span className={styles.durationLabel}>Durata Check-in</span>
                                <span className={styles.durationValue}>
                                  {(() => {
                                    const start = new Date(reportData.timing.firstScan);
                                    const end = new Date(reportData.timing.lastScan);
                                    const diff = Math.round((end - start) / (1000 * 60));
                                    const hours = Math.floor(diff / 60);
                                    const minutes = diff % 60;
                                    return hours > 0 
                                      ? `${hours}h ${minutes}min`
                                      : `${minutes} minute`;
                                  })()}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </section>
            </div>
          )}

          {/* Empty State */}
          {!selectedEvent && !loading && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <QrCodeScannerIcon />
              </div>
              <Typography variant="h6" className={styles.emptyTitle}>
                Selectează un eveniment
              </Typography>
              <Typography className={styles.emptyDescription}>
                Alege un eveniment din lista de mai sus pentru a vizualiza 
                raportul detaliat de check-in
              </Typography>
            </div>
          )}
        </div>
      </PageLayout>
    </>
  );
};

export default EventCheckinReport;
